// packages/web/src/lib/aging.ts — S14: the money layer's clock.
//
// Every aged number in the product goes through this file, so that "how
// old is this money" has exactly one answer and exactly one way of saying
// "we do not know".
//
// THE RULE THIS MODULE EXISTS TO ENFORCE: an absent date is NOT zero days
// and NOT today. Before S14 no invoice carried a date at all, so every
// imported row's only time signal was `created_at` — the day the import
// ran. Deriving an age from that would turn an import timestamp into a
// business fact, and an aged receivables report built on it would be
// wrong with a decimal point on it. So `ageInDays` returns null for a
// missing or unparseable date, and every band function puts null in its
// own `unknown` bucket rather than the newest band.
//
// DAY GRAIN, LOCAL DAYS. The pack stores these as `date` fields
// ('YYYY-MM-DD'), which is the grain the business thinks in ("the cheque
// came in on the 12th"). Ages are therefore whole days between two
// CALENDAR days, computed from the local Y/M/D of each side rather than
// from a millisecond subtraction — a UTC-midnight parse of '2026-08-12'
// is the 11th at 20:00 in Toronto, and a naive (now - then)/86400000
// would read one day older than the office does for half of every day.

/** A calendar day at local midnight, from either 'YYYY-MM-DD' or a full
 * ISO timestamp. Null when the value is absent or not a date at all —
 * never a guess. */
export function parseDay(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (ymd) {
    const [, y, m, d] = ymd;
    const day = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(day.getTime()) ? null : day;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/** Whole days from `value` to `now`. Null when the date is unknown.
 * Negative for a date in the future — a post-dated invoice is a real
 * thing and clamping it to 0 would hide a data-entry error. */
export function ageInDays(value: unknown, now: Date = new Date()): number | null {
  const then = parseDay(value);
  if (then === null) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today.getTime() - then.getTime()) / 86_400_000);
}

/** One aged bucket: how it reads, and which ages fall in it. `maxDays`
 * null means "and everything older". */
export interface AgeBandDef {
  key: string;
  label: string;
  /** Inclusive lower bound, in days. */
  minDays: number;
  /** Inclusive upper bound, in days; null = unbounded. */
  maxDays: number | null;
  /** A CSS custom property name — the severity ramp, oldest = loudest. */
  color: string;
}

/** wf02 §02's own receivable bands, verbatim: "0–30 days · 31–60 · 61–90 ·
 * 90+", on the status ramp it drew (neutral → warning → serious →
 * critical). A future-dated invoice (age < 0) lands in the first band with
 * everything else that is not yet 31 days old. */
export const RECEIVABLE_BANDS: readonly AgeBandDef[] = [
  { key: '0_30', label: '0–30 days', minDays: -Infinity, maxDays: 30, color: 'var(--status-neutral)' },
  { key: '31_60', label: '31–60', minDays: 31, maxDays: 60, color: 'var(--status-warning)' },
  { key: '61_90', label: '61–90', minDays: 61, maxDays: 90, color: 'var(--status-serious)' },
  { key: '90_plus', label: '90+', minDays: 91, maxDays: null, color: 'var(--status-critical)' },
];

/** S15 — THE DEPOSIT CLOCK, wf02 §02's own bands: "This week · 1–2 weeks ·
 * Over 2 weeks". A DIFFERENT scale from the receivable bands above, and
 * deliberately so: money sitting undeposited for a month is a different
 * problem from an invoice unpaid for a month, and the clock runs from the
 * day the money ARRIVED (hvac_payment.received_date), not the day it was
 * billed. Three bands, not four, because that is what the office does
 * about it — take it to the bank today, this week, or explain why not. */
export const DEPOSIT_BANDS: readonly AgeBandDef[] = [
  { key: 'this_week', label: 'This week', minDays: -Infinity, maxDays: 7, color: 'var(--status-neutral)' },
  { key: '1_2_weeks', label: '1–2 weeks', minDays: 8, maxDays: 14, color: 'var(--status-warning)' },
  { key: 'over_2_weeks', label: 'Over 2 weeks', minDays: 15, maxDays: null, color: 'var(--status-critical)' },
];

/** Which band an age belongs to. Null age -> null band: the caller must
 * decide what to say about a date nobody recorded, and this module will
 * not decide it for them by defaulting to the newest bucket. */
export function bandFor(
  ageDays: number | null,
  bands: readonly AgeBandDef[] = RECEIVABLE_BANDS
): AgeBandDef | null {
  if (ageDays === null) return null;
  for (const band of bands) {
    if (ageDays >= band.minDays && (band.maxDays === null || ageDays <= band.maxDays)) return band;
  }
  return null;
}

export interface AgedBucket extends AgeBandDef {
  count: number;
  total: number;
}

export interface AgedTally {
  bands: AgedBucket[];
  /** The rows whose date nobody recorded. Its own bucket, always — never
   * folded into a band, never dropped from the totals. */
  unknown: { count: number; total: number };
  /** Every row, aged or not: what the pile is actually worth. */
  count: number;
  total: number;
}

/** Buckets a pile of {date, amount} pairs into the aged bands plus the
 * honest unknown group. */
export function tallyByAge<T>(
  rows: readonly T[],
  dateOf: (row: T) => unknown,
  amountOf: (row: T) => number,
  now: Date = new Date(),
  bands: readonly AgeBandDef[] = RECEIVABLE_BANDS
): AgedTally {
  const buckets: AgedBucket[] = bands.map((b) => ({ ...b, count: 0, total: 0 }));
  const unknown = { count: 0, total: 0 };
  let count = 0;
  let total = 0;
  for (const row of rows) {
    const amount = amountOf(row);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    count += 1;
    total += safeAmount;
    const band = bandFor(ageInDays(dateOf(row), now), bands);
    const bucket = band === null ? null : buckets.find((b) => b.key === band.key);
    if (bucket) {
      bucket.count += 1;
      bucket.total += safeAmount;
    } else {
      unknown.count += 1;
      unknown.total += safeAmount;
    }
  }
  return { bands: buckets, unknown, count, total };
}

/** How an age reads on a record's own screen — wf01's "unpaid 41 days".
 * The unknown case says so in words rather than showing a number that
 * would be read as a small one. */
export function ageLabel(ageDays: number | null): string {
  if (ageDays === null) return 'age unknown';
  if (ageDays < 0) return 'dated ahead';
  if (ageDays === 0) return 'today';
  if (ageDays === 1) return '1 day';
  return `${ageDays} days`;
}
